import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import Input from '../../components/common/Input';
import {
  Plus,
  UserPlus,
  Edit3,
  Power,
  Mail,
  Phone,
  Briefcase,
  User,
  ShieldCheck,
  Clock,
} from 'lucide-react';

const SuperAdminAdmins = () => {
  const { theme } = useTheme();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    designation: '',
    company: '',
    city: '',
    state: '',
    country: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/admins');
      const data = response.data;
      setAdmins(data);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (admin = null) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        name: admin.name || '',
        email: admin.email || '',
        phone: admin.phone || '',
        designation: admin.designation || '',
        company: admin.company || '',
        city: admin.city || '',
        state: admin.state || '',
        country: admin.country || '',
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        designation: '',
        company: '',
        city: '',
        state: '',
        country: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAdmin(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      designation: '',
      company: '',
      city: '',
      state: '',
      country: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingAdmin) {
        const { email: _email, ...updatePayload } = formData;
        await api.patch(`/superadmin/admins/${editingAdmin.id}`, updatePayload);
      } else {
        await api.post('/superadmin/admins', formData);
      }
      handleCloseModal();
      fetchAdmins();
    } catch (err) {
      console.error('Failed to save admin:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const openToggleModal = (admin) => {
    setTogglingAdmin(admin);
    setShowToggleModal(true);
  };

  const handleConfirmToggle = async () => {
    if (!togglingAdmin) return;
    setToggling(true);
    try {
      await api.patch(`/superadmin/admins/${togglingAdmin.id}/toggle`, {
        isActive: !togglingAdmin.isActive,
      });
      setShowToggleModal(false);
      setTogglingAdmin(null);
      fetchAdmins();
    } catch (err) {
      console.error('Failed to update admin status:', err);
      setShowToggleModal(false);
      setTogglingAdmin(null);
    } finally {
      setToggling(false);
    }
  };

  const columns = [
    {
      key: 'admin',
      label: 'Admin',
      render: (admin) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: theme.colors.primary + '20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ShieldCheck size={20} color={theme.colors.primary} />
          </div>
          <div>
            <div style={{ fontWeight: '600', color: theme.colors.text }}>{admin.name}</div>
            <div
              style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '2px',
              }}
            >
              <Mail size={12} />
              {admin.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (admin) =>
        admin.phone ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.colors.text }}>
            <Phone size={14} color={theme.colors.textSecondary} />
            {admin.phone}
          </div>
        ) : (
          <span style={{ color: theme.colors.textSecondary }}>—</span>
        ),
    },
    {
      key: 'role',
      label: 'Position',
      render: (admin) =>
        admin.designation || admin.company ? (
          <div>
            {admin.designation && (
              <div style={{ color: theme.colors.text, fontWeight: '500' }}>{admin.designation}</div>
            )}
            {admin.company && (
              <div
                style={{
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '2px',
                }}
              >
                <Briefcase size={12} />
                {admin.company}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: theme.colors.textSecondary }}>—</span>
        ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (admin) => {
        const line = [admin.city, admin.state].filter(Boolean).join(', ');
        return line || admin.country ? (
          <div>
            {line && <div style={{ color: theme.colors.text, fontWeight: '500' }}>{line}</div>}
            {admin.country && (
              <div style={{ fontSize: '13px', color: theme.colors.textSecondary, marginTop: '2px' }}>
                {admin.country}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: theme.colors.textSecondary }}>—</span>
        );
      },
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      render: (admin) =>
        admin.lastLoginAt ? (
          <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
            {new Date(admin.lastLoginAt).toLocaleDateString()}
          </div>
        ) : (
          <div
            style={{
              fontSize: '12px',
              color: theme.colors.warning || '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Clock size={12} />
            Never logged in
          </div>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (admin) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Badge variant={admin.isActive ? 'success' : 'danger'} size="sm" status>
            {admin.isActive ? 'Active' : 'Inactive'}
          </Badge>
          {admin.mustSetPassword && (
            <Badge variant="warning" size="sm">
              Setup Pending
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (admin) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="sm"
            variant="ghost"
            icon={Edit3}
            onClick={() => handleOpenModal(admin)}
            title="Edit Admin"
          />
          <Button
            size="sm"
            variant="ghost"
            icon={Power}
            onClick={() => openToggleModal(admin)}
            title={admin.isActive ? 'Deactivate' : 'Activate'}
          />
        </div>
      ),
    },
  ];

  const styles = {
    container: {
      padding: '32px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '32px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      marginBottom: '24px',
    },
    actionBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    error: {
      padding: '12px 16px',
      borderRadius: '8px',
      backgroundColor: theme.colors.error + '15',
      color: theme.colors.error,
      fontSize: '14px',
      marginBottom: '16px',
      border: `1px solid ${theme.colors.error}30`,
    },
    success: {
      padding: '12px 16px',
      borderRadius: '8px',
      backgroundColor: theme.colors.success + '15',
      color: theme.colors.success,
      fontSize: '14px',
      marginBottom: '16px',
      border: `1px solid ${theme.colors.success}30`,
    },
    info: {
      padding: '12px 16px',
      borderRadius: '8px',
      backgroundColor: theme.colors.primary + '10',
      color: theme.colors.primary,
      fontSize: '13px',
      marginBottom: '16px',
      border: `1px solid ${theme.colors.primary}25`,
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
    },
    fullWidth: {
      gridColumn: '1 / -1',
    },
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading admins..." />;
  }

  return (
    <div style={styles.container}>
      {/* Action Bar */}
      <Card compact>
        <div style={styles.actionBar}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.text, margin: 0 }}>
            All Admins
          </h3>
          <Button icon={Plus} onClick={() => handleOpenModal()}>
            Add Admin
          </Button>
        </div>
      </Card>

      {/* Table */}
      <div style={{ marginTop: '24px' }}>
        <Card>
          {admins.length === 0 ? (
            <EmptyState
              icon={UserPlus}
              title="No admins added yet"
              description="Add the first admin. They will receive an email to set their password."
              action={() => handleOpenModal()}
              actionLabel="Add Admin"
            />
          ) : (
            <Table columns={columns} data={admins} loading={loading} emptyMessage="No admins found" />
          )}
        </Card>
      </div>

      {/* Create / Edit Admin Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingAdmin ? 'Edit Admin' : 'Add New Admin'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              {editingAdmin ? 'Save Changes' : 'Add Admin'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          {!editingAdmin && (
            <div style={styles.info}>
              A secure password setup link will be emailed to the new admin automatically. They
              must set their own password before their first login.
            </div>
          )}

          <div style={styles.formGrid}>
            <div style={styles.fullWidth}>
              <Input
                type="text"
                label="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter admin name"
                icon={User}
                required
              />
            </div>

            <Input
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="admin@example.com"
              icon={Mail}
              required
              disabled={!!editingAdmin}
              helperText={editingAdmin ? 'Email cannot be changed after creation.' : ''}
            />

            <Input
              type="tel"
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1 (555) 000-0000"
              icon={Phone}
            />

            {editingAdmin && (
              <>
                <Input
                  type="text"
                  label="Designation"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="e.g., Event Manager"
                  icon={Briefcase}
                />

                <Input
                  type="text"
                  label="Company / Organisation"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="e.g., Health Conference Ltd."
                  icon={Briefcase}
                />

                <Input
                  type="text"
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g., New Delhi"
                />

                <Input
                  type="text"
                  label="State"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="e.g., Delhi"
                />

                <div style={styles.fullWidth}>
                  <Input
                    type="text"
                    label="Country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="e.g., India"
                  />
                </div>
              </>
            )}
          </div>
        </form>
      </Modal>

      {/* Toggle Status Confirmation Modal */}
      <Modal
        isOpen={showToggleModal}
        onClose={() => { setShowToggleModal(false); setTogglingAdmin(null); }}
        title={togglingAdmin?.isActive ? 'Deactivate Admin' : 'Activate Admin'}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => { setShowToggleModal(false); setTogglingAdmin(null); }}
            >
              Cancel
            </Button>
            <Button
              variant={togglingAdmin?.isActive ? 'danger' : 'primary'}
              onClick={handleConfirmToggle}
              loading={toggling}
            >
              {togglingAdmin?.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </>
        }
      >
        <p style={{ color: theme.colors.text, margin: 0 }}>
          {togglingAdmin?.isActive
            ? `Deactivating ${togglingAdmin?.name} will prevent them from logging into the admin dashboard immediately. You can re-activate them at any time.`
            : `Activating ${togglingAdmin?.name} will restore their access to the admin dashboard.`}
        </p>
      </Modal>
    </div>
  );
};

export default SuperAdminAdmins;
